import { cn } from '@/platform/lib/utils';
import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    useClose,
} from '@headlessui/react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Link, usePage } from '@inertiajs/react';
import { useEffect, useMemo } from 'react';

const CloseWhenInactive = ({ active }) => {
    const close = useClose();

    useEffect(() => {
        if (!active) {
            close();
        }
    }, [active, close]);

    return null;
};

const NavigationItem = ({ item, isOpen = false, onToggle = () => {} }) => {
    const {
        component,
        props: { auth },
    } = usePage();

    const availableServices = useMemo(
        () =>
            new Set(
                auth?.customer?.available_services?.map(service => service.id)
            ),
        [auth?.customer?.available_services]
    );

    const Component = item?.hasInertia ? Link : 'a';

    if (item?.permissions && !item.permissions(auth?.permissions || [])) {
        return null;
    }

    return (
        <li>
            {!item.children ? (
                <Component
                    aria-current={
                        item?.components?.includes(component)
                            ? 'page'
                            : undefined
                    }
                    href={item?.href
                        ?.replace(':cid', auth?.user?.selected_customer_id || 0)
                        ?.replace(
                            ':eid',
                            auth?.customer?.entities?.length > 0
                                ? auth?.customer?.entities[0].id
                                : 0
                        )}
                    className={cn(
                        'group flex gap-x-3 border-l-4 border-transparent px-4 py-2 text-sm/6 text-gray-500 transition-colors duration-200',
                        item?.components?.includes(component) &&
                            'border-primary-600 text-primary-600',
                        !item?.components?.includes(component) &&
                            'hover:border-gray-500 hover:text-gray-700'
                    )}
                >
                    {item.label}
                </Component>
            ) : (
                <Disclosure as='div' defaultOpen={isOpen}>
                    <CloseWhenInactive active={isOpen} />
                    <DisclosureButton
                        onClick={onToggle}
                        className={cn(
                            'group flex w-full items-center gap-x-3 border-l-4 border-transparent px-4 py-2 text-left text-sm/6 text-gray-500 transition-colors duration-200',
                            item?.components?.includes(component) &&
                                'border-primary-600 text-primary-600',
                            !item?.components?.includes(component) &&
                                'hover:border-gray-500 hover:text-gray-700 data-open:border-gray-500 data-open:text-gray-700'
                        )}
                    >
                        {item.label}
                        <ChevronRightIcon
                            aria-hidden='true'
                            className={cn(
                                'ml-auto size-5 shrink-0 text-gray-400 group-data-open:rotate-90',
                                item?.components?.includes(component) &&
                                    'text-primary-600',
                                !item?.components?.includes(component) &&
                                    'group-data-open:text-gray-500'
                            )}
                        />
                    </DisclosureButton>
                    <DisclosurePanel
                        transition
                        as='div'
                        className='grid grid-rows-[1fr] transition-all duration-200 ease-out data-closed:grid-rows-[0fr] data-closed:opacity-0'
                    >
                        <ul className='mt-1 overflow-hidden px-2'>
                            {item.children.map(subItem => {
                                if (
                                    subItem?.permissions &&
                                    !subItem.permissions(
                                        auth?.permissions || []
                                    )
                                ) {
                                    return null;
                                }

                                if (
                                    subItem?.services &&
                                    !subItem?.services?.some(service =>
                                        availableServices.has(service)
                                    )
                                ) {
                                    return null;
                                }

                                const SubComponent = subItem?.hasInertia
                                    ? Link
                                    : 'a';
                                const isCurrent =
                                    subItem?.components?.includes(component);

                                return (
                                    <li key={subItem.label}>
                                        <SubComponent
                                            href={subItem?.href
                                                ?.replace(
                                                    ':cid',
                                                    auth?.user
                                                        ?.selected_customer_id ||
                                                        0
                                                )
                                                ?.replace(
                                                    ':eid',
                                                    auth?.customer?.entities
                                                        ?.length > 0
                                                        ? auth?.customer
                                                              ?.entities[0].id
                                                        : 0
                                                )}
                                            aria-current={
                                                isCurrent ? 'page' : undefined
                                            }
                                            className={cn(
                                                'hover:text-primary-600 block rounded-md py-0.5 pr-2 pl-9 text-sm/6 text-gray-600 transition-colors duration-200',
                                                isCurrent &&
                                                    'text-primary-600 font-semibold'
                                            )}
                                        >
                                            {subItem.label}
                                        </SubComponent>
                                    </li>
                                );
                            })}
                        </ul>
                    </DisclosurePanel>
                </Disclosure>
            )}
        </li>
    );
};

export default NavigationItem;
